import { TRACE_PROCESSOR } from "@dxos/tracing";
import { scheduleTask, scheduleTaskInterval } from "@dxos/async";
import { Context } from "@dxos/context";
//#region src/extensions/otel/intervals.ts
/** How often metrics are collected and exported, on either side of the worker port. */
var METRIC_EXPORT_INTERVAL = 6e4;
//#endregion
//#region src/extensions/otel/remote-metrics.ts
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/observability/src/extensions/otel/remote-metrics.ts";
/** Projects tags onto OTel attributes, dropping nullish values that are not valid attribute values. */
var metricDataToAttributes = (data) => {
	const tags = data?.tags;
	if (!tags) return {};
	return Object.entries(tags).reduce((attributes, [key, value]) => {
		if (value !== null && value !== void 0) attributes[key] = value;
		return attributes;
	}, {});
};
/**
* Forwards TRACE_PROCESSOR metrics to the observability worker. Hot producers record per message,
* so records are aggregated per series and posted as one batch per interval rather than one
* postMessage per call.
*/
var RemoteMetricsForwarder = class {
	#post;
	#ctx = new Context(void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 47
	});
	#batchInterval;
	#maxValues;
	#pending = /* @__PURE__ */ new Map();
	#pendingValues = 0;
	#flushScheduled = false;
	#processor = {
		increment: (name, value, data) => this.#record("increment", name, value ?? 1, metricDataToAttributes(data), data),
		distribution: (name, value, data) => this.#record("distribution", name, value, metricDataToAttributes(data), data),
		set: () => {},
		gauge: (name, value, data) => this.#record("gauge", name, value, metricDataToAttributes(data), data),
		observe: (name, callback, data) => this.observe(name, callback, metricDataToAttributes(data), data)
	};
	constructor(post, options = {}) {
		this.#post = post;
		this.#batchInterval = options.batchInterval ?? 1e3;
		this.#maxValues = options.maxValues ?? 1e4;
		TRACE_PROCESSOR.remoteMetrics.registerProcessor(this.#processor);
	}
	gauge(name, value, tags, meta) {
		this.#record("gauge", name, value, tags, meta);
	}
	increment(name, value, tags, meta) {
		this.#record("increment", name, value ?? 1, tags, meta);
	}
	distribution(name, value, tags, meta) {
		this.#record("distribution", name, value, tags, meta);
	}
	observe(name, callback, tags, meta) {
		const ctx = this.#ctx.derive();
		scheduleTaskInterval(ctx, async () => {
			const value = callback();
			if (value === void 0 || !Number.isFinite(value)) return;
			this.#record("gauge", name, value, tags, meta);
		}, METRIC_EXPORT_INTERVAL);
		return () => {
			ctx.dispose();
		};
	}
	/**
	* Post everything recorded so far as one batch. Call before asking the worker to flush, so the
	* batch is ahead of the flush on the port.
	*/
	flush() {
		if (this.#pending.size === 0) return;
		const metrics = [...this.#pending.values()];
		this.#pending.clear();
		this.#pendingValues = 0;
		this.#post({
			type: "otel-metric-batch",
			metrics
		});
	}
	async close() {
		TRACE_PROCESSOR.remoteMetrics.unregisterProcessor(this.#processor);
		this.flush();
		await this.#ctx.dispose();
	}
	#record(op, name, value, tags, meta) {
		const key = `${op}\0${name}\0${tags === void 0 ? "" : JSON.stringify(tags)}`;
		const pending = this.#pending.get(key);
		if (pending === void 0) {
			this.#pending.set(key, {
				op,
				name,
				values: [value],
				tags,
				...meta?.unit !== void 0 || meta?.description !== void 0 ? { meta: {
					unit: meta.unit,
					description: meta.description
				} } : {}
			});
			this.#pendingValues++;
		} else switch (op) {
			case "increment":
				pending.values[0] += value;
				break;
			case "gauge":
				pending.values[0] = value;
				break;
			case "distribution":
				pending.values.push(value);
				this.#pendingValues++;
		}
		if (this.#pendingValues >= this.#maxValues) this.flush();
		else this.#scheduleFlush();
	}
	#scheduleFlush() {
		if (this.#flushScheduled || this.#ctx.disposed) return;
		this.#flushScheduled = true;
		scheduleTask(this.#ctx, () => {
			this.#flushScheduled = false;
			this.flush();
		}, this.#batchInterval);
	}
};
//#endregion
export { metricDataToAttributes as n, METRIC_EXPORT_INTERVAL as r, RemoteMetricsForwarder as t };

//# sourceMappingURL=chunk-remote-metrics.mjs.map